import { t } from "i18next";
import { get, isEmpty, isNumber, isUndefined, toNumber } from "lodash";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Exams } from "../api/exams";
import { User } from "../api/user";
import { CreateChukkerDto } from "../dto/createChukkerDto";
import { Chukker, Chukkers } from "../shared/types/chukkers.type";
import { TQualifyChoice } from "../shared/types/qualifyChoices.type";
import {
  TEquimetre,
  Ttraining,
  TTrainingEcgData,
} from "../shared/types/training.type";
import { hydraTransform } from "../shared/utils/hydraTransformer";
import { RootState } from "../store";
import { UseFileDownload } from "./useFileDownload";
import { useLocales } from "./useLocales";
import { Fleet } from "../shared/types/fleet.type";

export type TPdfReport = {
  filename: string;
  url: string;
};

export const useExam = (
  enableQuery: boolean = false,
  trainer?: string | null,
  examId?: number | null,
  horse?: string,
  examsQueryParams?: string[],
) => {
  const { currentLang } = useLocales();

  const [examsLength, setExamsLength] = useState(0);
  const restricted = useSelector<RootState>(
    (state) => state.user.restrictedMode,
  ) as boolean;
  const { download, isDownloading } = UseFileDownload();
  const [isEcgDownloading, setIsEcgDownloading] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [isExamExporting, setIsExamExporting] = useState(false);
  //choices Mutations
  const getTrainingChoicesMutation = useMutation((id: number) =>
    Exams.getChoices(id),
  );
  const deleteExamMutation = useMutation((id: number) =>
    Exams.deleteTraining(id),
  );
  const editTrainingMutation = useMutation(
    (data: { id: number; data: TQualifyChoice; enablePostProcess: boolean }) =>
      Exams.editTraining(data.id, data.data, data.enablePostProcess),
  );
  const editTrainingsMutation = useMutation((data: any) =>
    Exams.editTrainings(data),
  );
  const getQualifyChoices = useMutation((id?: number) =>
    User.getQualifyChoices(id, currentLang),
  );
  const downloadExamReportMutation = useMutation((id: number) =>
    Exams.downloadExamReport(id),
  );
  const exportExamPageMutation = useMutation((id: number) =>
    Exams.exportExamPage(id),
  );
  const exportXlsxMutation = useMutation(
    (ids: number[]) => Exams.exportXlsx(ids),
    { retry: 10, retryDelay: 5 * 1000 },
  );
  const processExamMutation = useMutation((id: number) =>
    Exams.processTraining(id),
  );

  //Create Chukker Mutations
  const createChukkerMutation = useMutation(
    (data: (Chukker | CreateChukkerDto)[]) => Exams.createChukker(data),
  );

  const downloadComparaisonPdf = useMutation(
    ({ examIds, insightParams }: { examIds: string; insightParams: string }) =>
      Exams.downloadComparaisonPdf(examIds, insightParams),
  );
  const downloadAdvancedComparaisonPdf = useMutation(
    ({ examIds, userId }: { examIds: string; userId: string }) =>
      Exams.downloadAdvancedComparaisonPdf(examIds, userId),
  );

  //get Exam Mutation
  const getHorseExams = useMutation(
    () =>
      Exams.getExams(trainer, horse, ["additional-groups[]=training:insight"]),
    {},
  );

  const downloadExamReport = async (id?: number) => {
    if (!id) return;

    try {
      setIsPdfDownloading(true);

      const response = await downloadExamReportMutation.mutateAsync(id);

      await download(response.data.url, response.data.filename);

      return response.data;
    } catch (err) {
      toast(t("error:generic"), { type: "error" });
      return err;
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const exportExamPage = (id?: number) => {
    if (!id) return;
    setIsExamExporting(true);
    exportExamPageMutation
      .mutateAsync(id)
      .then(async (response) => {
        await download(response.data.url, response.data.filename);
      })
      .catch((err) => {
        toast(t("error:generic"), { type: "error" });
      })
      .finally(() => {
        setIsExamExporting(false);
      });
  };

  const exportXlsxData = (ids: number[]) => {
    if (isEmpty(ids)) return;
    setIsExamExporting(true);
    exportXlsxMutation
      .mutateAsync(ids)
      .then(async (response) => {
        await download(response.data.file_url, response.data.filename);
      })
      .catch((err) => {
        toast(t("error:generic"), { type: "error" });
      })
      .finally(() => {
        setIsExamExporting(false);
      });
  };

  //comparaison Mutation
  const compareMutation = useMutation(
    ({
      ids,
      date,
      options,
      allKeyParam,
      exclueIncompleteTrainings,
    }: {
      ids: string;
      date?: string;
      options?: string;
      allKeyParam?: boolean;
      exclueIncompleteTrainings?: boolean;
    }) =>
      Exams.getTrainingsKeyParams(
        ids,
        date,
        trainer,
        options,
        allKeyParam,
        exclueIncompleteTrainings,
        currentLang,
      ),
  );

  const query = useQuery(
    ["exams", JSON.stringify(examsQueryParams), trainer],
    () => Exams.getExams(trainer, undefined, examsQueryParams),
    {
      enabled: enableQuery && !isUndefined(trainer),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onSuccess: ({ data }) => {
        setExamsLength(get(data, "hydra:totalItems", 0));
      },
    },
  );

  const examQuery = useQuery(
    ["exam", toNumber(examId)],
    () => Exams.getExam(toNumber(examId), restricted),
    {
      enabled: isNumber(examId),
      refetchOnWindowFocus: false,
      cacheTime: 900000,
      onSuccess: ({ data, ...other }) => {},
    },
  );

  const getChoices = async (id: number) => {
    try {
      let result = await getTrainingChoicesMutation.mutateAsync(id);
      return result.data;
    } catch (error) {
      throw error;
    }
  };
  const renderTrainingTypeAuto = (training: Ttraining) => {
    const training_type = training.training_type?.display_name;
    const training_type_auto = training.training_type_automatic?.display_name;

    if (training_type) {
      // Wrap in a span to keep structure consistent
      return `<span>${training_type}</span>`;
    }

    if (training_type_auto) {
      return `<i>${training_type_auto}</i>`;
    }

    return `<span>-</span>`;
  };

  const renderTrainingTrackAuto = (training: Ttraining) => {
    let training_track = training.track_name?.display_name;
    let training_track_auto = training.track_name_automatic?.display_name;

    return training_track ?? `<i>${training_track_auto ?? ""}</i>`;
  };

  const downloadEcg = async (data?: TTrainingEcgData) => {
    if (!data) return;
    setIsEcgDownloading(true);
    await download(
      data.download_link,
      data.filename,
      "ecg.downloading",
      "report:ecgDownloadError",
    );
    setIsEcgDownloading(false);
  };

  const getEqmTitle = (equimtre?: TEquimetre | Fleet) => {
    if (!equimtre) return " - ";

    if (equimtre?.display_name) {
      return equimtre.display_name;
    } else {
      let str = "EQUIMETRE_";

      let splitedMac = equimtre.mac_address.mac_address.split("-");
      console.log({ splitedMac, mac: equimtre.mac_address.mac_address });

      let splitedMacLength = splitedMac.length;

      let last4Char = splitedMac.slice(splitedMacLength - 2).join("");

      return str.concat(last4Char);
    }
  };

  const saveExamChukker = async (data: any[]): Promise<Chukkers> => {
    try {
      let response = await createChukkerMutation.mutateAsync(data);

      return response.data.chukkers;
    } catch (error) {
      throw error;
    }
  };

  return {
    exams: hydraTransform(query.data?.data ?? []),
    exam: examQuery.data?.data,
    horseExams: hydraTransform(getHorseExams.data?.data ?? []),
    examHasError: examQuery.isError,
    examError: examQuery.error,
    getChoices,
    compare: compareMutation,
    deleteExam: deleteExamMutation,
    editTrainingMutation,
    getHorseExams,
    isItemToEditLoading: getTrainingChoicesMutation.isLoading,
    isItemEditing: editTrainingMutation.isLoading,
    isExamLoading: examQuery.isLoading,
    isExamsLoading: query.isLoading || query.isFetching,
    getQualifyChoices,
    editTrainings: editTrainingsMutation,
    examsLength,
    examQuery,
    examsQuery: query,
    renderTrainingTypeAuto,
    renderTrainingTrackAuto,
    downloadExamReport,
    reportLoading: downloadExamReportMutation.isLoading || isDownloading,
    downloadEcg,
    getEqmTitle,
    downloadComparaisonPdf,
    isEcgDownloading,
    isPdfDownloading,
    exportExamPage,
    isExamExporting,
    processExamMutation,
    saveExamChukker,
    isChukkersLoading: createChukkerMutation.isLoading,
    downloadAdvancedComparaisonPdf,
    exportXlsxData,
    isExportingXlsx: exportXlsxMutation.isLoading,
  };
};
